<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import flatpickr from 'flatpickr'
  import 'flatpickr/dist/flatpickr.min.css'

  export let value: string | undefined = undefined
  export let placeholder: string = 'Select date'
  export let minDate: string | undefined = undefined
  export let maxDate: string | undefined = undefined
  export let disabled: boolean = false
  export let onChange: ((date: string | undefined) => void) | undefined = undefined

  let inputElement: HTMLInputElement
  let flatpickrInstance: flatpickr.Instance | null = null

  function parseToDate(value?: string): Date | undefined {
    if (!value) return undefined
    // Handle d/m/Y (e.g., 05/10/2025)
    if (value.includes('/')) {
      const parts = value.split('/')
      if (parts.length === 3) {
        const [dayStr, monthStr, yearStr] = parts
        const day = parseInt(dayStr, 10)
        const month = parseInt(monthStr, 10)
        const year = parseInt(yearStr, 10)
        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          return new Date(year, month - 1, day)
        }
      }
    }
    // Handle ISO yyyy-mm-dd
    if (value.includes('-')) {
      const d = new Date(value)
      if (!isNaN(d.getTime())) return d
    }
    // Fallback
    const d = new Date(value)
    return isNaN(d.getTime()) ? undefined : d
  }

  onMount(() => {
    // Set default to today's date if no value is provided
    const defaultDate = parseToDate(value) || new Date()
    
    flatpickrInstance = flatpickr(inputElement, {
      dateFormat: 'd/m/Y',
      allowInput: true,
      clickOpens: !disabled,
      disableMobile: false,
      defaultDate: defaultDate,
      minDate: parseToDate(minDate),
      maxDate: parseToDate(maxDate),
      onChange: (selectedDates, dateStr) => {
        value = dateStr
        if (onChange) onChange(dateStr)
      }
    })
  })

  onDestroy(() => {
    if (flatpickrInstance) {
      flatpickrInstance.destroy()
    }
  })

  // Update flatpickr when props change
  $: if (flatpickrInstance) {
    flatpickrInstance.set('minDate', parseToDate(minDate))
    flatpickrInstance.set('maxDate', parseToDate(maxDate))
    flatpickrInstance.set('clickOpens', !disabled)
  }
</script>

<input 
  bind:this={inputElement}
  type="text" 
  {placeholder}
  {disabled}
  class="date-picker-input"
  readonly
/>

<style>
  .date-picker-input {
    border: 1px solid #d3d3d3;
    padding: 6px 10px;
    max-width: 120px;
    font-size: 13px;
    font-weight: 400;
    background: white;
    cursor: pointer;
  }

  .date-picker-input:focus {
    border-color: #1d1d1f;
    outline: none;
  }

  .date-picker-input:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }

  /* Custom Flatpickr styling to match your design */
  :global(.flatpickr-calendar) {
    font-family: inherit;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }

  :global(.flatpickr-day.selected) {
    background: #1d1d1f;
    border-color: #1d1d1f;
  }

  :global(.flatpickr-day:hover) {
    background: #f0f0f0;
  }

  /* Grey out disabled/unavailable days */
  :global(.flatpickr-day.disabled),
  :global(.flatpickr-day.notAllowed),
  :global(.flatpickr-day.prevMonthDay),
  :global(.flatpickr-day.nextMonthDay) {
    color: #b3b3b3;
    opacity: 0.6;
    cursor: not-allowed;
    background: #fff;
  }
</style>
